import { useState } from 'react'
import { generateControlCard, previewControlCard, finalizeControlCard } from '@/features/control-card'
import type { GenerateControlCardRequest, GenerateControlCardResponse } from '@/features/control-card'

/**
 * Hook for generating control card PDFs
 * 
 * @example
 * ```tsx
 * const { generate, isGenerating, error } = useControlCard()
 * 
 * const handleGenerate = async () => {
 *   const result = await generate({
 *     equipmentId: 'uuid-123',
 *     inspection: {
 *       jobType: 'Periodisk kontroll',
 *       performedDate: new Date().toISOString(),
 *       performedBy: 'John Doe',
 *       responsiblePerson: 'Jane Smith',
 *       status: 'OK',
 *     },
 *   })
 *   
 *   if (result.success) {
 *     // Download the PDF
 *     downloadBlob(result.blob!, 'control-card.pdf')
 *   }
 * }
 * ```
 */
export function useControlCard() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Generate control card (preview or finalized)
   */
  const generate = async (
    request: GenerateControlCardRequest
  ): Promise<GenerateControlCardResponse> => {
    setIsGenerating(true)
    setError(null)

    try {
      const result = await generateControlCard(request)

      if (!result.success) {
        setError(result.error || 'Failed to generate control card')
      }

      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      return {
        success: false,
        error: errorMessage,
      }
    } finally {
      setIsGenerating(false)
    }
  }

  /**
   * Generate preview (with watermark, no report number)
   */
  const preview = async (
    request: GenerateControlCardRequest
  ): Promise<GenerateControlCardResponse> => {
    setIsGenerating(true)
    setError(null)

    try {
      const result = await previewControlCard(request)

      if (!result.success) {
        setError(result.error || 'Failed to generate preview')
      }

      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      return {
        success: false,
        error: errorMessage,
      }
    } finally {
      setIsGenerating(false)
    }
  }

  /**
   * Finalize control card (assigns report number, no watermark)
   */
  const finalize = async (
    request: GenerateControlCardRequest
  ): Promise<GenerateControlCardResponse> => {
    setIsGenerating(true)
    setError(null)

    try {
      const result = await finalizeControlCard(request)

      if (!result.success) {
        setError(result.error || 'Failed to finalize control card')
      }

      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      return {
        success: false,
        error: errorMessage,
      }
    } finally {
      setIsGenerating(false)
    }
  }

  /**
   * Generate and download control card
   */
  const generateAndDownload = async (
    request: GenerateControlCardRequest,
    filename?: string
  ): Promise<boolean> => {
    const result = await generate(request)

    if (result.success && result.blob) {
      const url = URL.createObjectURL(result.blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename || `control-card-${request.equipmentId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      return true
    }

    return false
  }

  /**
   * Preview and download control card
   */
  const previewAndDownload = async (
    request: GenerateControlCardRequest,
    filename?: string
  ): Promise<boolean> => {
    const result = await preview(request)

    if (result.success && result.blob) {
      const url = URL.createObjectURL(result.blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename || `control-card-preview-${request.equipmentId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      return true
    }

    return false
  }

  /**
   * Finalize and download control card
   */
  const finalizeAndDownload = async (
    request: GenerateControlCardRequest,
    filename?: string
  ): Promise<boolean> => {
    const result = await finalize(request)

    if (result.success && result.blob) {
      const url = URL.createObjectURL(result.blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename || `control-card-${result.reportNumber || request.equipmentId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      return true
    }

    return false
  }

  return {
    generate,
    preview,
    finalize,
    generateAndDownload,
    previewAndDownload,
    finalizeAndDownload,
    isGenerating,
    error,
  }
}

